import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'next-i18next';
import { useSwrInterviewAnswerDelete, useSwrUserInterviewList } from '@hooks/useSwrApi/endpoints/interview';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { ShowMorePill, Tooltip } from '@components/common';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { DestructiveAction } from '@components/common/DestructiveAction/DestructiveAction';
import { TrashIcon } from '@heroicons/react/24/solid';
import { ApiSchema } from '@type/api/apiUtilityFn';
import {
  CvDetailInterface,
} from '../interface/CvDetailInterface';

type IInterviewUserQuestionRead = ApiSchema<'InterviewUserQuestionRead'>;

interface ProfileInterviewProps extends CvDetailInterface {
  locale: Locale;
  /** Include answers from all jobs; honored by the backend only for the logged-in user's own list. */
  allJobs?: boolean;
  /** Override the heading style, e.g. to match the surrounding page's block headings. */
  headingClassName?: string;
  /** Override the question style, e.g. to match the video interview question rows. */
  questionClassName?: string;
  /** Show a per-answer delete action. Only for the logged-in user's own list (profile edit page). */
  hasDelete?: boolean;
  /** Wrap the answers in a scrollable container, e.g. to match the video interview list. */
  listClassName?: string;
}

export const ProfileInterview = ({
  cvDetail, jobSlug, locale, allJobs, hasDelete, listClassName,
  headingClassName = 'mb-3 text-lg font-bold',
  questionClassName = 'mb-2 text-sm font-bold',
}: ProfileInterviewProps) => {
  const { t } = useTranslation('common');
  const { locale: routerLocale } = useExtendedRouter();
  const { isAuth } = useAuthClient();
  const finalLocale = locale || routerLocale || 'ro';

  // Fetch interview list with infinite pagination
  // For CV detail: if jobSlug is provided (applicant view), filter by job; otherwise show all interviews
  // Pass userSlug to fetch interviews for the specific user (CV owner)
  const {
    data,
    size,
    setSize,
    isLoading,
    isEmpty,
    showLoadMore,
    mutate,
  } = useSwrUserInterviewList({
    locale: finalLocale,
    jobSlug: jobSlug || null, // jobSlug - null for general view, jobSlug for applicant view
    userSlug: cvDetail?.slug || null, // userSlug - pass the CV owner's slug to fetch their interviews
    allJobs,
    shouldFetch: isAuth, // endpoint requires authentication - skip the request for guests
  });

  // Per-answer delete (own list only): set the id, the effect fires the request
  const [answerToDelete, setAnswerToDelete] = useState<string | null>(null);
  const { trigger: triggerDeleteAnswer } = useSwrInterviewAnswerDelete({
    locale: finalLocale,
    answerId: answerToDelete ?? '',
    successCallback: () => {
      setAnswerToDelete(null);
      void mutate();
    },
    errorCallback: () => {
      setAnswerToDelete(null);
    },
  });

  useEffect(() => {
    if (!answerToDelete) return;
    void triggerDeleteAnswer();
  }, [answerToDelete, triggerDeleteAnswer]);

  // Flatten the paginated data into a single array
  const allInterviews = useMemo(() => {
    if (!data) return [];
    return data.flatMap((page) => page?.items || []);
  }, [data]);

  // How many answers are still hidden, for the pill's +N label. Unlike the interview videos
  // (all in memory, revealed in one go), this list is paginated by the API — loading page by
  // page keeps one click = one request, even for accounts with hundreds of answers.
  const remainingCount = Math.max((data?.at(-1)?.total ?? 0) - allInterviews.length, 0);

  // The pill loads the second page and opts into auto-loading: from then on, a sentinel under
  // the list fetches the next page whenever it scrolls into view, one page at a time.
  // hasMorePages comes straight from the last response — the middleware's showLoadMore state
  // lags one render behind it, which would let the effect overshoot with one extra request.
  const [autoLoad, setAutoLoad] = useState(false);
  const { ref: sentinelRef, inView } = useInView({ rootMargin: '100px' });
  const hasMorePages = (data?.at(-1)?.page ?? 1) < (data?.at(-1)?.pages ?? 1);

  useEffect(() => {
    if (autoLoad && inView && hasMorePages && !isLoading) {
      void setSize(size + 1);
    }
  }, [autoLoad, inView, hasMorePages, isLoading, size, setSize]);

  // Don't render if no interviews and not loading
  if (!isLoading && isEmpty && !allInterviews.length) {
    return null;
  }

  return (
    <>
      {allInterviews.length > 0 && (
        <h3 className={headingClassName}>{t('cv.detail.mini_interview')}</h3>
      )}

      <div className={listClassName}>
        {allInterviews.map((item: IInterviewUserQuestionRead) => (
          <div key={item.questionId || item.question} className="mb-4 border-b">
            <div className="flex items-start justify-between gap-2">
              <div className={questionClassName}>{item.question}</div>
              {hasDelete && item.textAnswer?.id && (
                <Tooltip
                  floating
                  useFloatingPortal
                  tooltipText={t('ai.interview.delete')}
                  colorScheme="black"
                  position="bottom"
                  className="relative leading-none"
                >
                  <DestructiveAction
                    withConfirmation
                    confirmationDescription={t('ai.interview.delete.confirmation')}
                    styling="text"
                    color="error"
                    onConfirm={async () => {
                      setAnswerToDelete(item.textAnswer?.id ?? null);
                    }}
                  >
                    <TrashIcon className="size-5" />
                  </DestructiveAction>
                </Tooltip>
              )}
            </div>

            {/* Text answer */}
            {item.textAnswer?.answerText ? (
              <blockquote className="my-4 ml-1 border-l-4 border-gray-300 pl-4">
                <div className="my-3 text-sm font-normal">{item.textAnswer.answerText}</div>
              </blockquote>
            ) : (
              <blockquote className="my-4 ml-1 border-l-4 border-gray-300 pl-4">
                <div className="my-3 text-sm font-normal text-gray-400">
                  {t('cv.detail.interview.no_answer')}
                </div>
              </blockquote>
            )}
          </div>
        ))}

        {/* Sentinel: once auto-loading is on, scrolling to the end of the list fetches the next
            page. It must sit inside the (possibly scrollable) list so it only enters the view
            when the user actually reaches the bottom. */}
        {autoLoad && showLoadMore && <div ref={sentinelRef} aria-hidden />}
        {autoLoad && showLoadMore && isLoading && (
          <div className="mt-2 flex justify-center"><TailwindSpinner /></div>
        )}
      </div>

      {/* Load-more pill — same control as under the interview videos list, but paginated */}
      {!autoLoad && showLoadMore && (
        <ShowMorePill
          onClick={() => {
            setAutoLoad(true);
            void setSize(size + 1);
          }}
          isLoading={isLoading}
        >
          {`${t('global.label.load_more')}${remainingCount > 0 ? ` +${remainingCount}` : ''}`}
        </ShowMorePill>
      )}
    </>
  );
};
